package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.example.positionahead.model.AlertType
import com.example.positionahead.model.PriceAlert

import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PlannerGoalType
import com.example.positionahead.model.PortfolioObjective
import com.example.positionahead.model.RiskTolerance
import com.example.positionahead.ui.components.CollapsibleSection

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsScreen(
    apiKey: String,
    usesLiveQuotes: Boolean,
    onSaveApiKey: (String) -> Unit,
    alerts: List<PriceAlert>,
    onCreateAlert: (String, AlertType, Double, String) -> Unit,
    onDeleteAlert: (String) -> Unit,
    onToggleAlert: (String, Boolean) -> Unit,
    onClearTriggeredAlert: (String) -> Unit,
    goal: FinancialGoal,
    currentPortfolioValue: Double,
    onSaveGoal: (
        plannerGoalType: PlannerGoalType,
        customGoalLabel: String?,
        targetAmount: Double,
        targetDateYears: Int,
        monthlyContribution: Double,
        age: Int,
        riskTolerance: RiskTolerance,
        objective: PortfolioObjective,
        maxSinglePositionPercent: Double,
        targetYieldPercent: Double,
        investmentHorizonYears: Int,
    ) -> Unit,
    expandGoalsOnOpen: Boolean = false,
    onGoalsExpanded: () -> Unit = {},
    modifier: Modifier = Modifier,
) {
    var apiKeyInput by remember(apiKey) { mutableStateOf(apiKey) }
    var showGoals by remember { mutableStateOf(expandGoalsOnOpen) }

    LaunchedEffect(expandGoalsOnOpen) {
        if (expandGoalsOnOpen) {
            showGoals = true
            onGoalsExpanded()
        }
    }

    Column(modifier = modifier.fillMaxSize()) {
        TopAppBar(title = { Text("Settings") })

        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            Text("Connection", style = MaterialTheme.typography.titleMedium)
            Text(
                if (usesLiveQuotes) {
                    "Connected to Financial Modeling Prep for live market prices."
                } else {
                    "Using demo prices. Add your API key from financialmodelingprep.com to enable live quotes."
                },
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
            )

            OutlinedTextField(
                value = apiKeyInput,
                onValueChange = { apiKeyInput = it },
                label = { Text("FMP API key") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )

            Button(
                onClick = { onSaveApiKey(apiKeyInput) },
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text("Save API key")
            }

            HorizontalDivider()

            CollapsibleSection(
                title = "Financial Goals",
                expanded = showGoals,
                onToggle = { showGoals = !showGoals }
            ) {
                GoalsScreen(
                    goal = goal,
                    currentPortfolioValue = currentPortfolioValue,
                    onSaveGoal = onSaveGoal,
                    modifier = Modifier.fillMaxWidth(),
                    embedded = true
                )
            }

            HorizontalDivider()

            AlertsSection(
                alerts = alerts,
                onCreateAlert = onCreateAlert,
                onDeleteAlert = onDeleteAlert,
                onToggleAlert = onToggleAlert,
                onClearTriggered = onClearTriggeredAlert,
            )
        }
    }
}
